import { getSchedulingWindow } from '../endpoints/getSchedulingWindow';
import { evaluateSchedulingWindow, type OpenPeriod } from '../endpoints/evaluateSchedulingWindow';
import { acceptDegraded } from 'validation';

export interface OpenPeriodsPreview {
  /** The window as saved, for the heading — the caller may only know its id. */
  readonly windowName: string;
  readonly zoneName: string;
  /** The zone the evaluation ran in, and therefore the one the periods are read in. */
  readonly timezone: string;
  readonly periods: readonly OpenPeriod[];
}

/**
 * When a window's openings can be previewed from, and the periods that follow.
 *
 * Orchestrates two endpoints because the evaluate route needs a timezone and the
 * caller only has a row: the window is read first for its saved zone, then
 * evaluated in it. Sending no timezone is not an option — the backend then falls
 * back to a hardcoded `Europe/Amsterdam`.
 *
 * **Evaluates the window as saved.** Nothing here can preview unsaved edits: the
 * route resolves the window by zone and name and reads the database. That is why
 * the preview is reached from the list rather than from inside the edit dialog,
 * where it would look like feedback on the rules currently on screen.
 *
 * Deliberately impure — it evaluates from "now", so the same arguments give a
 * different answer as time passes. Callers that cache it must re-ask rather than
 * compare inputs.
 */
export const previewOpenPeriods = async (id: number): Promise<OpenPeriodsPreview> => {
  const answer = await getSchedulingWindow(id);
  // An operation may word its own messages — it knows its use case, which an
  // endpoint does not. The two failures need different sentences because they
  // need different actions: a refresh fixes a stale list row, and nothing the
  // operator can do fixes a response we cannot read.
  if (answer.result === 'unreadable') {
    throw new Error('The scheduling window could not be read. Please report this.');
  }
  // A preview is a viewer, so it shows a degraded record rather than refusing one: a
  // substituted display field is worth less than the periods the operator came for. The
  // same read backs the EDIT dialog, which refuses it — that asymmetry is the point of
  // leaving the decision to the caller.
  const record = answer.result === 'ok' ? answer.value : answer.degradedValue;
  if (record === null) {
    throw new Error('Scheduling window not found; the UI may be out of date. Please refresh.');
  }
  const window = record;
  if (window.zone_name === null) {
    throw new Error(`Scheduling window '${window.name}' has no zone, so it cannot be evaluated.`);
  }

  const { schedule_open_in_given_period } = acceptDegraded(
    await evaluateSchedulingWindow({
      zoneName: window.zone_name,
      windowName: window.name,
      timestamp: new Date().toISOString(),
      timezone: window.timezone,
    })
  );

  return {
    windowName: window.name,
    zoneName: window.zone_name,
    timezone: window.timezone,
    periods: schedule_open_in_given_period,
  };
};
